import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Ban, Check, ChevronDown, ChevronRight, Hand, Lock } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { CompanyActionLevel, CompanyToolPolicy, DelegationAction, ToolDelegation } from '../../../types/aai';
import { EmptyState, ErrorNote, Loading, Select } from '../../aa';

export type PermissionMode = 'company' | 'personal';
type Level = 'allow' | 'approval' | 'never';

interface PermAction {
  key: string;
  label: string;
  touches?: string;
  risk: string;
  level: Level | null;
  locked: string | null;
}

const RISK_GROUPS: { risk: string; title: string; desc: string }[] = [
  { risk: 'read', title: 'Looking', desc: 'Reads only. Changes nothing and sends nothing.' },
  { risk: 'draft', title: 'Preparing', desc: 'Writes something for the person to check. Nothing goes out until they send or save it.' },
  { risk: 'act', title: 'Doing things as the person', desc: 'Changes records or sends things in that person\'s name.' },
  { risk: 'destructive', title: 'Deleting', desc: 'Removes things for good. An assistant never does this for anyone.' },
];

const LEVELS: Level[] = ['allow', 'approval', 'never'];

const LEVEL_LABEL: Record<PermissionMode, Record<Level, string>> = {
  company: { allow: 'Allowed, each person decides', approval: 'Needs approval every time', never: 'Never, for anyone' },
  personal: { allow: 'Allowed', approval: 'Needs my approval', never: 'Never' },
};

const LEVEL_ICON: Record<Level, React.ReactNode> = {
  allow: <Check size={14} strokeWidth={2.5} />,
  approval: <Hand size={14} strokeWidth={2.2} />,
  never: <Ban size={14} strokeWidth={2.2} />,
};

const COMPANY_TO_LEVEL: Record<CompanyActionLevel, Level> = { person: 'allow', approval: 'approval', off: 'never' };
const LEVEL_TO_COMPANY: Record<Level, CompanyActionLevel> = { allow: 'person', approval: 'approval', never: 'off' };
const DECISION_TO_LEVEL: Record<'yes' | 'approval' | 'no', Level> = { yes: 'allow', approval: 'approval', no: 'never' };
const LEVEL_TO_DECISION: Record<Level, 'yes' | 'approval' | 'no'> = { allow: 'yes', approval: 'approval', never: 'no' };

type PersonalAction = DelegationAction & { company_level?: CompanyActionLevel | null };

const fromCompany = (p: CompanyToolPolicy): PermAction[] => p.actions.map((a) => ({
  key: a.key, label: a.label, touches: a.touches, risk: a.risk,
  level: a.fixed ? 'never' : COMPANY_TO_LEVEL[a.level],
  locked: a.fixed,
}));

const fromPersonal = (d: ToolDelegation): PermAction[] => (d.actions as PersonalAction[]).map((a) => {
  let locked: string | null = null;
  let level: Level | null = a.decision ? DECISION_TO_LEVEL[a.decision] : null;
  if (a.company_level === 'approval') { locked = 'Needs approval, set by the company'; level = 'approval'; }
  if (a.company_level === 'off') { locked = 'Off for the company'; level = 'never'; }
  if (a.blocked) { locked = a.blocked; level = 'never'; }
  if (a.risk === 'destructive') { locked = 'Never run by an assistant'; level = 'never'; }
  return { key: a.key, label: a.label, touches: a.touches, risk: a.risk, level, locked };
});

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

const Segment: React.FC<{ value: Level | null; disabled: boolean; mode: PermissionMode; onPick: (l: Level) => void }> = ({ value, disabled, mode, onPick }) => (
  <span role="radiogroup" className={`inline-flex rounded-lg border border-line bg-canvas p-0.5 ${disabled ? 'opacity-60' : ''}`}>
    {LEVELS.map((l) => {
      const on = value === l;
      const tone = { true: 'bg-white text-ink shadow-sm', false: 'text-ink-soft hover:text-ink' }[String(on)];
      return (
        <button
          key={l}
          type="button"
          role="radio"
          aria-checked={on}
          title={LEVEL_LABEL[mode][l]}
          aria-label={LEVEL_LABEL[mode][l]}
          disabled={disabled}
          onClick={() => onPick(l)}
          className={`grid h-7 w-8 place-items-center rounded-md transition-colors disabled:cursor-not-allowed ${tone}`}
        >
          {LEVEL_ICON[l]}
        </button>
      );
    })}
  </span>
);

const Group: React.FC<{
  title: string; desc: string; rows: PermAction[]; mode: PermissionMode; saving: string;
  onSet: (keys: string[], level: Level) => void;
}> = ({ title, desc, rows, mode, saving, onSet }) => {
  const [open, setOpen] = useState(true);
  const free = rows.filter((r) => !r.locked);
  const first = free[0]?.level || null;
  const uniform = free.length > 0 && free.every((r) => r.level === first);
  const groupValue = uniform && first ? first : 'mixed';
  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-2 text-left">
          {open ? <ChevronDown size={16} className="text-ink-soft" /> : <ChevronRight size={16} className="text-ink-soft" />}
          <span className="text-[15px] font-semibold text-ink">{title}</span>
          <span className="inline-flex h-[21px] items-center rounded-full bg-canvas-inset px-2 text-[11px] font-semibold tabular-nums text-ink-muted">{rows.length}</span>
        </button>
        {free.length > 0 && (
          <Select
            value={groupValue}
            aria-label={`${title}: set every action`}
            disabled={!!saving}
            onChange={(e) => onSet(free.map((r) => r.key), e.target.value as Level)}
            className="!h-9 !w-auto !py-0 text-[13px]"
          >
            {groupValue === 'mixed' && <option value="mixed">Mixed</option>}
            {LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[mode][l]}</option>)}
          </Select>
        )}
      </div>
      <p className="m-0 mt-1 pl-6 text-[12.5px] leading-[19px] text-ink-dim">{desc}</p>
      {open && (
        <div className="mt-2 divide-y divide-line-soft border-y border-line-soft">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center justify-between gap-4 py-3 pl-6 pr-1">
              <div className="min-w-0">
                <div className="text-[13.5px] text-ink">{r.label}</div>
                {r.touches && <div className="text-[12px] text-ink-dim">Reaches {r.touches}.</div>}
                {r.locked && <div className="inline-flex items-center gap-1 text-[12px] text-ink-soft"><Lock size={11} /> {r.locked}</div>}
              </div>
              <Segment value={r.level} disabled={!!r.locked || saving === r.key} mode={mode} onPick={(l) => onSet([r.key], l)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ToolPermissions: React.FC<{ tool: string; label: string; mode: PermissionMode }> = ({ tool, label, mode }) => {
  const [rows, setRows] = useState<PermAction[] | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    let live = true;
    setRows(null);
    setError('');
    const load = mode === 'company'
      ? aaiApi.getCompanyToolPolicy(tool).then((r) => fromCompany(r.data))
      : aaiApi.getDelegation(tool).then((r) => fromPersonal(r.data));
    load.then((next) => { if (live) setRows(next); })
      .catch(() => { if (live) setError(`Could not load what ${label} can do.`); });
    return () => { live = false; };
  }, [tool, label, mode]);

  const saveOne = async (key: string, level: Level) => {
    if (mode === 'company') {
      const { data } = await aaiApi.setCompanyActionLevel(tool, key, LEVEL_TO_COMPANY[level]);
      return fromCompany(data);
    }
    const { data } = await aaiApi.recordDelegation(tool, key, LEVEL_TO_DECISION[level]);
    return fromPersonal(data);
  };

  const set = async (keys: string[], level: Level) => {
    setSaving(keys.length === 1 ? keys[0] : 'group');
    try {
      let next: PermAction[] | null = null;
      for (const key of keys) next = await saveOne(key, level);
      if (next) setRows(next);
      toast.success(`Saved. ${LEVEL_LABEL[mode][level]}.`);
    } catch (err) {
      toast.error(detailOf(err, 'That permission did not save.'));
    } finally {
      setSaving('');
    }
  };

  const intro = mode === 'company'
    ? `Choose what an assistant may do in ${label} for the whole company. People still choose for themselves inside what you allow.`
    : `Choose when your assistant may use these tools. You only see what your company has given you access to.`;

  return (
    <section className="mt-7">
      <h3 className="m-0 text-[17px] font-semibold text-ink">Tool permissions</h3>
      <p className="m-0 mt-1 text-[13.5px] leading-[21px] text-ink-muted">{intro}</p>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-dim">
        {LEVELS.map((l) => <span key={l} className="inline-flex items-center gap-1.5"><span className="text-ink-soft">{LEVEL_ICON[l]}</span>{LEVEL_LABEL[mode][l]}</span>)}
      </div>
      {!rows && !error && <Loading label="Loading actions" />}
      {error && <ErrorNote message={error} />}
      {rows && rows.length === 0 && (
        <EmptyState title="No actions to govern yet" reason={`We could not read what ${label} can do, so there is nothing to allow or switch off here.`} />
      )}
      {rows && RISK_GROUPS.map((g) => {
        const group = rows.filter((r) => r.risk === g.risk);
        if (group.length === 0) return null;
        return <Group key={g.risk} title={g.title} desc={g.desc} rows={group} mode={mode} saving={saving} onSet={set} />;
      })}
    </section>
  );
};
